/* V193 — Auto Sitemap Ping Admin UI
 * Daily cron that pings Google Indexing API for fresh blog + Local SEO pages.
 * Admin can preview pending URLs, force-run, and review daily history.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminSitemapPingTab() {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [maxUrls, setMaxUrls] = useState(40);
  const [force, setForce] = useState(false);
  const [err, setErr] = useState("");
  const [lastRun, setLastRun] = useState(null);

  const token = () => localStorage.getItem("admin_token") || "";

  const load = async () => {
    if (!token()) { setErr("Admin token chýba. Prihlás sa znova."); return; }
    setLoading(true); setErr("");
    try {
      const [p, h] = await Promise.all([
        axios.get(`${API}/auto-sitemap-ping/preview`, { headers: { "X-Admin-Token": token() } }),
        axios.get(`${API}/auto-sitemap-ping/history?limit=14`, { headers: { "X-Admin-Token": token() } }),
      ]);
      setPreview(p.data);
      setHistory(h.data.runs || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!token()) return;
    setRunning(true); setErr(""); setLastRun(null);
    try {
      const r = await axios.post(
        `${API}/auto-sitemap-ping/run-now?max_urls=${maxUrls}&force=${force}`, {},
        { headers: { "X-Admin-Token": token() } });
      setLastRun(r.data);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "run_failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="p-4 space-y-5" data-testid="sitemap-ping-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          📡 Auto Sitemap Ping
          <span className="text-[10px] tracking-widest bg-amber-500 text-black px-2 py-0.5 font-black uppercase">V193</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Denný cron (04:00 UTC) pingne Google Indexing API pre top-N čerstvých blog článkov + Local SEO stránok. Doplnok k V167.11 (/audit pages).
        </p>
      </header>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="sitemap-ping-error">
          {err}
        </div>
      )}

      {/* Run controls */}
      <section className="border-2 border-zinc-800 bg-gradient-to-br from-zinc-950 to-black p-5" data-testid="sitemap-ping-controls">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Manual run</div>
            <div className="text-sm text-zinc-300 mt-1">Idempotent per UTC date; <code className="bg-zinc-800 px-1">force=true</code> obíde cache.</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-400">Max URLs</label>
            <input type="number" min="1" max="100" value={maxUrls}
                   onChange={(e) => setMaxUrls(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                   data-testid="sitemap-ping-max"
                   className="w-20 bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 text-sm font-mono" />
          </div>
          <label className="text-xs text-zinc-400 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                   data-testid="sitemap-ping-force" />
            Force (bypass cache)
          </label>
          <button onClick={runNow} disabled={running}
                  data-testid="sitemap-ping-run-btn"
                  className="text-sm px-5 py-2 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
            {running ? "Pingujem…" : "🚀 Run Now"}
          </button>
          <button onClick={load} className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            🔄 Refresh
          </button>
        </div>

        {lastRun && (
          <div className="mt-4 border border-amber-700 bg-amber-950/30 p-3 text-xs" data-testid="sitemap-ping-last-run">
            <div className="font-black uppercase tracking-wider text-amber-300 mb-1">Posledný run</div>
            <div className="text-zinc-200">
              <span className="text-emerald-400">✓ {lastRun.sent || 0} sent</span>
              {" · "}
              <span className="text-rose-400">✗ {lastRun.failed || 0} failed</span>
              {" · "}
              <span className="text-zinc-400">⊘ {lastRun.disabled || 0} disabled</span>
              {" · status: "}<span className="font-mono">{lastRun.status}</span>
              {" · "}<span className="font-mono">blog={lastRun.blog_count} · seo={lastRun.seo_count}</span>
            </div>
          </div>
        )}
      </section>

      {loading && <div className="text-zinc-500 text-sm">Načítavam…</div>}

      {/* Preview */}
      {preview && (
        <section className="border border-zinc-800 bg-zinc-950 p-4" data-testid="sitemap-ping-preview">
          <h3 className="text-sm uppercase tracking-widest text-zinc-500 font-black mb-2">
            🔍 Preview — čo by sa pinglo ďalším runom
            <span className="ml-2 text-amber-300 font-mono">{preview.total} URL</span>
            <span className="ml-2 text-zinc-600 text-[10px]">(lookback {preview.lookback_days}d · cap {preview.max_urls})</span>
          </h3>

          {preview.total === 0 ? (
            <div className="text-zinc-500 text-sm py-2">
              🎉 Žiadne nové URL. Všetky čerstvé blog + Local SEO stránky sú už pingnuté.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-emerald-400 font-black uppercase tracking-wider text-[10px] mb-1">
                  📝 Blog ({preview.blog_urls.length})
                </div>
                <ul className="space-y-1 max-h-64 overflow-auto">
                  {preview.blog_urls.map((u) => (
                    <li key={u} className="text-zinc-300 font-mono truncate" title={u}>
                      • <a href={u} target="_blank" rel="noopener noreferrer" className="hover:text-amber-300">{u}</a>
                    </li>
                  ))}
                  {preview.blog_urls.length === 0 && <li className="text-zinc-600 italic">— žiadne —</li>}
                </ul>
              </div>
              <div>
                <div className="text-blue-400 font-black uppercase tracking-wider text-[10px] mb-1">
                  🏪 Local SEO ({preview.seo_urls.length})
                </div>
                <ul className="space-y-1 max-h-64 overflow-auto">
                  {preview.seo_urls.map((u) => (
                    <li key={u} className="text-zinc-300 font-mono truncate" title={u}>
                      • <a href={u} target="_blank" rel="noopener noreferrer" className="hover:text-amber-300">{u}</a>
                    </li>
                  ))}
                  {preview.seo_urls.length === 0 && <li className="text-zinc-600 italic">— žiadne —</li>}
                </ul>
              </div>
            </div>
          )}
        </section>
      )}

      {/* History */}
      <section className="border border-zinc-800 bg-zinc-950" data-testid="sitemap-ping-history">
        <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm uppercase tracking-widest text-zinc-500 font-black">📅 História (14 dní)</h3>
          <span className="text-[10px] text-zinc-600">{history.length} runov</span>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-zinc-500 text-sm">Zatiaľ žiadne runy. Cron pobeží zajtra 04:00 UTC.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Blog</th>
                <th className="text-right p-2">SEO</th>
                <th className="text-right p-2">Sent</th>
                <th className="text-right p-2">Failed</th>
                <th className="text-right p-2">Disabled</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.date} className="border-t border-zinc-800" data-testid={`sitemap-ping-row-${row.date}`}>
                  <td className="p-2 font-mono text-zinc-300">{row.date}</td>
                  <td className="p-2">
                    <span className={`text-[10px] px-2 py-0.5 font-black uppercase tracking-wider ${
                      row.status === "ok" ? "bg-emerald-700 text-white" :
                      row.status === "empty" ? "bg-zinc-700 text-zinc-300" :
                      "bg-amber-700 text-white"
                    }`}>{row.status}</span>
                  </td>
                  <td className="p-2 text-right font-mono">{row.blog_count}</td>
                  <td className="p-2 text-right font-mono">{row.seo_count}</td>
                  <td className="p-2 text-right font-mono text-emerald-300">{row.sent}</td>
                  <td className="p-2 text-right font-mono text-rose-300">{row.failed}</td>
                  <td className="p-2 text-right font-mono text-zinc-500">{row.disabled}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed">
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">ℹ️ Ako to funguje</h3>
        <ul className="space-y-1 list-disc list-inside">
          <li>Cron <code className="bg-zinc-800 px-1">auto_sitemap_ping_daily</code> beží <strong className="text-zinc-200">04:00 UTC denne</strong>.</li>
          <li>Vyberie top čerstvé URL z posledných 7 dní z <code className="bg-zinc-800 px-1">blog_posts</code> + <code className="bg-zinc-800 px-1">local_seo_business_enrichments</code> ktoré ešte neboli pingnuté.</li>
          <li>50/50 budget split medzi blogom a Local SEO (slack ide tej strane ktorá má dost obsahu).</li>
          <li>Cap <strong className="text-zinc-200">40 URL/deň</strong> — V167.11 stále má 160/200 quoty pre /audit pages.</li>
          <li>Po úspešnom pingu sa zapíše <code className="bg-zinc-800 px-1">sitemap_pinged_at</code> field — žiadny duplicate ping.</li>
          <li>Idempotent per UTC date cez <code className="bg-zinc-800 px-1">auto_sitemap_ping_runs</code> collection.</li>
        </ul>
      </section>
    </div>
  );
}
